import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import type { GoogleContact } from '@/services/contactsService';
import { contactsService } from '@/services/contactsService';
import { Building2, User, Users } from 'lucide-react';
import React, { useState, useEffect, useCallback, useRef } from 'react';

interface EmailAutocompleteProps {
    value: string;
    onChange: (value: string) => void;
    onSelect?: (contact: GoogleContact) => void;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
}

/**
 * Get initials from name or email
 */
const getInitials = (name?: string, email?: string): string => {
    if (name) {
        const parts = name.split(' ').filter(Boolean);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    }
    if (email) {
        return email.substring(0, 2).toUpperCase();
    }
    return 'U';
};

/**
 * Get source icon for contact
 */
const SourceIcon: React.FC<{ source: GoogleContact['source'] }> = ({ source }) => {
    switch (source) {
        case 'directory':
            return <Building2 className="h-3 w-3 text-blue-500" />;
        case 'contacts':
            return <Users className="h-3 w-3 text-green-500" />;
        default:
            return <User className="h-3 w-3 text-gray-500" />;
    }
};

export const EmailAutocomplete: React.FC<EmailAutocompleteProps> = ({
    value,
    onChange,
    onSelect,
    placeholder = 'email@example.com',
    disabled = false,
    className = '',
}) => {
    const [open, setOpen] = useState(false);
    const [contacts, setContacts] = useState<GoogleContact[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const debounceRef = useRef<NodeJS.Timeout | null>(null);

    // Search contacts when input changes
    const searchContacts = useCallback(async (query: string) => {
        if (!query || query.length < 2) {
            setContacts([]);
            setHasSearched(false);
            return;
        }

        setIsLoading(true);
        try {
            const results = await contactsService.searchContacts(query);
            setContacts(results);
            setHasSearched(true);
        } catch (error) {
            console.error('Error searching contacts:', error);
            setContacts([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Debounced search
    useEffect(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            searchContacts(value);
        }, 300);

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [value, searchContacts]);

    // Handle contact selection
    const handleSelect = (contact: GoogleContact) => {
        onChange(contact.email);
        onSelect?.(contact);
        setOpen(false);
        setContacts([]);
        setHasSearched(false);
    };

    // Handle input change
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        onChange(newValue);
        if (newValue.length >= 2) {
            setOpen(true);
        } else {
            setOpen(false);
        }
    };

    // Handle key down
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
            setOpen(false);
        }
        if (e.key === 'ArrowDown' && contacts.length > 0) {
            setOpen(true);
        }
    };

    // Close popover when clicking outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (inputRef.current && !inputRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const showPopover = open && (contacts.length > 0 || (hasSearched && !isLoading));

    return (
        <div className={`relative ${className}`} ref={inputRef}>
            <Input
                value={value}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                    if (value.length >= 2 && contacts.length > 0) {
                        setOpen(true);
                    }
                }}
                placeholder={placeholder}
                disabled={disabled}
                className="w-full"
                autoComplete="off"
            />
            
            {showPopover && (
                <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg overflow-hidden">
                    {isLoading ? (
                        <div className="p-3 text-sm text-muted-foreground text-center">
                            Searching contacts...
                        </div>
                    ) : contacts.length === 0 ? (
                        <div className="p-3 text-sm text-muted-foreground text-center">
                            <p>No contacts found</p>
                            <p className="text-xs mt-1 opacity-70">
                                Try signing out and back in to enable contacts
                            </p>
                        </div>
                    ) : (
                        <ul className="max-h-[250px] overflow-y-auto">
                            {contacts.map((contact, index) => (
                                <li
                                    key={`${contact.email}-${index}`}
                                    className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-accent transition-colors"
                                    onClick={() => handleSelect(contact)}
                                >
                                    <Avatar className="h-8 w-8">
                                        <AvatarImage src={contact.photo} alt={contact.name || contact.email} />
                                        <AvatarFallback className="text-xs">
                                            {getInitials(contact.name, contact.email)}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1 min-w-0">
                                        {contact.name && (
                                            <div className="text-sm font-medium truncate">
                                                {contact.name}
                                            </div>
                                        )}
                                        <div className="text-xs text-muted-foreground truncate">
                                            {contact.email}
                                        </div>
                                    </div>
                                    <SourceIcon source={contact.source} />
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
};

export default EmailAutocomplete;

